// src/pages/shelbula/workspaces/TerminalPanel.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronsRight, Loader, Server, Trash2, X, AlertTriangle } from 'lucide-react';
import { apiManager } from './apiManager';

/* ========================
 * TerminalOutput - Renders a single line of terminal output
 * ========================*/
const TerminalOutput = ({ line }) => {
    const renderWithAnsi = (text) => {
        const ansiRegex = /\u001b\[([0-9]{1,2}(;[0-9]{1,2})?)?[m|K]/g;
        const parts = text.split(ansiRegex);
        return parts.map((part, index) => {
            if (index % 3 === 1) return null;
            return <span key={index}>{part}</span>;
        });
    };

    if (line.type === 'command') {
        return (
            <div className="flex items-center">
                <ChevronsRight size={14} className="mr-2 text-primary flex-shrink-0" />
                <span className="flex-1 whitespace-pre-wrap">{line.text}</span>
            </div>
        );
    }
    
    const isError = line.source === 'stderr';
    return (
        <div className={`whitespace-pre-wrap ${isError ? 'text-red-400' : 'text-on-surface-dim'}`}>
            {renderWithAnsi(line.text)}
        </div>
    );
};

/* ========================
 * TerminalPanel - A modal, interactive terminal for a workspace
 * ========================*/
const TerminalPanel = ({ isOpen, onClose, workspaceId, initialPath }) => {
    const [history, setHistory] = useState([]);
    const [input, setInput] = useState('');
    const [isExecuting, setIsExecuting] = useState(false);
    const [isTerminalAvailable, setIsTerminalAvailable] = useState(true);
    const [commandHistory, setCommandHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const hasRunInitialCommand = useRef(false);

    const endOfPanelRef = useRef(null);
    const inputRef = useRef(null);
    
    const executeCommand = useCallback(async (commandToExecute) => {
        if (!commandToExecute.trim() || !workspaceId || !isTerminalAvailable) return;

        setIsExecuting(true);
        setHistory(prev => [...prev, { type: 'command', text: commandToExecute }]);
        if (commandToExecute !== `cd "${initialPath}"`) {
            setCommandHistory(prev => [commandToExecute, ...prev]);
        }
        setHistoryIndex(-1);

        try {
            const { stdout, stderr } = await apiManager.executeCommand(workspaceId, commandToExecute);
            if (stdout) {
                 setHistory(prev => [...prev, { type: 'output', text: stdout, source: 'stdout' }]);
            }
            if (stderr) {
                 setHistory(prev => [...prev, { type: 'output', text: stderr, source: 'stderr' }]);
            }
        } catch (error) {
            const errorMessage = error.message || 'An unknown error occurred.';
            if (error?.status === 501) {
                setIsTerminalAvailable(false);
            }
            setHistory(prev => [...prev, { type: 'output', text: errorMessage, source: 'stderr' }]);
        } finally {
            setIsExecuting(false);
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [workspaceId, initialPath, isTerminalAvailable]);

    useEffect(() => {
        if (isOpen) {
            // Reset state when opening
            hasRunInitialCommand.current = false;
            setIsTerminalAvailable(true);
            setHistory([]);
            const welcome = { type: 'output', text: 'Shelbula Cloud Terminal connected.', source: 'stdout' };
            const availability = { type: 'output', text: 'This terminal UI is mounted, but command execution depends on backend support in the current workspace server checkout.', source: 'stdout' };
            const whitelist = { type: 'output', text: 'Allowed commands include: ls, cat, cd, pwd, rm, mv, and package management (npm, pnpm, yarn).', source: 'stdout' };
            
            const initialCd = `cd "${initialPath}"`;
            if (initialPath && !hasRunInitialCommand.current) {
                setHistory([welcome, availability, whitelist]);
                hasRunInitialCommand.current = true;
                executeCommand(initialCd);
            } else {
                 setHistory([welcome, availability, whitelist]);
            }
            
            // Focus input when panel opens
            setTimeout(() => inputRef.current?.focus(), 100);

        }
    }, [isOpen, initialPath, executeCommand]);

    useEffect(() => {
        endOfPanelRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [history]);
    
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, onClose]);


    const handleFormSubmit = (e) => {
        e.preventDefault();
        executeCommand(input);
        setInput('');
    };
    
    const handleClear = () => {
        setHistory([{ type: 'output', text: 'Terminal cleared.', source: 'stdout' }]);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (commandHistory.length > 0) {
                const newIndex = Math.min(commandHistory.length - 1, historyIndex + 1);
                setHistoryIndex(newIndex);
                setInput(commandHistory[newIndex] || '');
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (historyIndex >= 0) {
                const newIndex = Math.max(-1, historyIndex - 1);
                setHistoryIndex(newIndex);
                setInput(commandHistory[newIndex] || '');
            }
        } else if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
             if (isExecuting) {
                setIsExecuting(false);
                setHistory(prev => [...prev, {type: 'output', text: '^C', source: 'stdout'}]);
             }
        }
    };

    if (typeof document === 'undefined') return null;

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-[rgba(var(--background-rgb),0.5)] backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onMouseDown={(event) => event.stopPropagation()}
                        className="relative w-full max-w-4xl h-[70vh] bg-[rgba(var(--background-rgb),0.8)] border border-border-color rounded-lg shadow-2xl flex flex-col overflow-hidden"
                    >
                        {/* Terminal Header */}
                        <div className="flex-shrink-0 h-[40px] bg-surface border-b border-border-color flex items-center justify-between px-4">
                            <div className="flex items-center gap-2 text-sm font-semibold">
                                <Server size={16} className="text-primary" />
                                <span>Cloud Terminal</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={handleClear} className="p-1.5 rounded-md text-on-surface-dim hover:bg-surface-dark hover:text-on-surface" title="Clear Terminal">
                                    <Trash2 size={16} />
                                </button>
                                <button onClick={onClose} className="p-1.5 rounded-md text-on-surface-dim hover:bg-surface-dark hover:text-on-surface" title="Close Terminal (Esc)">
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Terminal Output */}
                        <div 
                            className="flex-1 p-4 overflow-y-auto custom-scrollbar font-mono text-sm"
                            onClick={() => inputRef.current?.focus()}
                        >
                            {history.map((line, index) => (
                                <TerminalOutput key={index} line={line} />
                            ))}
                            <div ref={endOfPanelRef} />
                        </div>

                        {/* Terminal Input */}
                        <div className="flex-shrink-0 p-2 border-t border-border-color">
                            {!isTerminalAvailable ? (
                                <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                                    <span>Terminal execution is not hooked up in this backend checkout yet. The UI opens, but the server route returns unsupported.</span>
                                </div>
                            ) : null}
                            <form onSubmit={handleFormSubmit} className="flex items-center">
                                {isExecuting ? (
                                    <Loader size={14} className="mr-2 text-primary-light animate-spin" />
                                ) : (
                                    <ChevronsRight size={14} className="mr-2 text-primary" />
                                )}
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="flex-1 pl-2 p-1 rounded-sm bg-transparent text-on-surface font-mono text-sm focus:outline-none"
                                    placeholder={isTerminalAvailable ? 'Enter command...' : 'Terminal execution unavailable in this checkout'}
                                    disabled={isExecuting || !workspaceId || !isTerminalAvailable}
                                    autoComplete="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                />
                            </form>
                        </div>
                   </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default TerminalPanel;